/**
 * "Create chatter account" — login, chatterprofiel en rechten in één keer.
 *
 * 27-09-2026 (Jordy). Hier stond een dialoog dat alleen een chatterprofiel
 * aanmaakte en de rechten daarna in een PATCH naar "de gekoppelde login"
 * stuurde — maar een nieuw profiel heeft geen login, dus elk vinkje ging
 * verloren. Nu, zoals "Create regular chatter account" op oud: één verzoek
 * naar POST /api/admin/auth/chatter-accounts, dat login, profiel, rechten,
 * modellen en (voor de owner) de head chatter in één transactie vastlegt.
 */
import { useState } from 'react'
import { Field, Modal } from './Modal'
import { mut, useActions } from '../lib/actions'
import { RechtenBlok } from './RechtenBlok'
import { standaardTokens, vlaggenUitTokens } from '../lib/rechtenBlok'
import { ModelKeuze, type ModelOptie } from './ModelKeuze'
import { TELEGRAM_ID_HINT, telegramIdFout } from '../lib/telegramId'

// 05-10-2026: de rechtenlijst staat in lib/rechtenBlok.ts (gedeeld met Users en Edit chatter).

export function ChatterAccountDialog({
  isOwner,
  leads,
  personas,
  onClose,
  onSaved,
}: {
  isOwner: boolean
  leads: { id: number; name: string; lead_override_pct: number | null }[]
  personas: ModelOptie[]
  onClose: () => void
  onSaved: () => void
}) {
  const { run, busy } = useActions()
  const [naam, setNaam] = useState('')
  const [mail, setMail] = useState('')
  const [wachtwoord, setWachtwoord] = useState('')
  const [tgUser, setTgUser] = useState('')
  const [tgChat, setTgChat] = useState('')
  // 05-10-2026 (Jordy): de rechten in het gedeelde blok (zelfde als Settings →
  // Users en Edit chatter). Alleen een owner wijzigt ze; anders de standaard.
  const [pages, setPages] = useState<string[]>(() => standaardTokens('chatter'))
  const [modellen, setModellen] = useState<number[]>([])
  const [lead, setLead] = useState('')
  const [referral, setReferral] = useState('')

  // 2-11-2026 (Jordy): Telegram-id's alleen als getal; de server controleert het ook.
  const tgProbleem = telegramIdFout(tgUser, 'Telegram user ID') || telegramIdFout(tgChat, 'Telegram Chat ID')
  const probleem = tgProbleem || (!naam.trim() ? 'Enter a username / chatter name.'
    : !/^[^@\s]+@[^@\s]+$/.test(mail.trim()) ? 'Enter a valid email address.'
      : wachtwoord.length < 8 ? 'The temporary password needs at least 8 characters.'
        : referral !== '' && (Number(referral) < 0 || Number(referral) > 100 || Number.isNaN(Number(referral)))
          ? 'Head referral % must be between 0 and 100.'
          : '')

  const body: Record<string, unknown> = {
    username: naam.trim(),
    email: mail.trim(),
    password: wachtwoord,
    display_name: naam.trim(),
    chatter_name: naam.trim(),
    telegram_user_id: tgUser.trim() || null,
    telegram_chat_id: tgChat.trim() || null,
    model_persona_ids: modellen,
    ...(isOwner ? vlaggenUitTokens(pages, false) : {}),
  }
  // Alleen de owner kiest een head chatter; de backend weigert het anders.
  if (isOwner && lead) {
    body.lead_chatter_id = Number(lead)
    if (referral !== '') body.head_referral_pct = Number(referral)
  }

  return (
    <Modal
      title="Create chatter account"
      desc="Login, chatter profile and permissions in one step, like the old dashboard."
      width={640}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn primary sm"
            type="button"
            disabled={!!probleem || busy === 'Create chatter account'}
            onClick={async () => {
              const gelukt = await run({
                label: 'Create chatter account',
                request: mut.post('/api/admin/auth/chatter-accounts', body),
                successText: `${naam.trim()} created`,
              })
              if (gelukt) { onSaved(); onClose() }
            }}
          >
            Create chatter
          </button>
        </>
      }
    >
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Account</div>
      <Field label="Username / chatter name">
        <input className="inp" value={naam} autoComplete="off"
               onChange={(e) => setNaam(e.target.value)} />
      </Field>
      <Field label="Email">
        <input className="inp" type="email" value={mail} autoComplete="off"
               onChange={(e) => setMail(e.target.value)} />
      </Field>
      <Field label="Temporary password" hint="At least 8 characters. The chatter can change it via 🔑.">
        <input className="inp" type="password" value={wachtwoord} autoComplete="new-password"
               onChange={(e) => setWachtwoord(e.target.value)} />
      </Field>
      <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="Telegram user ID (optional)" hint={TELEGRAM_ID_HINT}>
          <input className="inp" inputMode="numeric" value={tgUser} onChange={(e) => setTgUser(e.target.value)} />
        </Field>
        <Field label="Telegram chat ID (optional)" hint={TELEGRAM_ID_HINT}>
          <input className="inp" inputMode="numeric" value={tgChat} onChange={(e) => setTgChat(e.target.value)} />
        </Field>
      </div>
      {tgProbleem ? (
        <div className="mut2" style={{ fontSize: 12, color: '#fca5a5' }}>{tgProbleem}</div>
      ) : null}

      <div className="divh" />
      <RechtenBlok rol="chatter" pages={pages} onChange={setPages} magWijzigen={isOwner} />

      <div className="divh" />
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Assigned models</div>
      <ModelKeuze modellen={personas} gekozen={modellen} onChange={setModellen} />

      {isOwner ? (
        <>
          <div className="divh" />
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>Head chatter assignment</div>
          <div className="cols" style={{ gridTemplateColumns: '2fr 1fr', gap: 8 }}>
            <Field label="Reports to head chatter (optional)"
                   hint="The referral only counts for sales made after the account is created.">
              <select className="inp" value={lead} onChange={(e) => {
                setLead(e.target.value)
                const l = leads.find((x) => String(x.id) === e.target.value)
                setReferral(l && l.lead_override_pct != null ? String(l.lead_override_pct) : '')
              }}>
                <option value="">No head chatter</option>
                {leads.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Head referral %" hint="Stored on the head chatter.">
              <input className="inp" type="number" min={0} max={100} step="0.5" value={referral}
                     disabled={!lead} onChange={(e) => setReferral(e.target.value)} />
            </Field>
          </div>
        </>
      ) : null}

      {probleem && !tgProbleem && (naam || mail || wachtwoord)
        ? <div className="mut2" style={{ color: '#fca5a5', fontSize: 12, marginTop: 6 }}>{probleem}</div>
        : null}
    </Modal>
  )
}
